import { SkuType } from './skuDomain';

/**
 * How well a SKU's size matches the design being placed. Declaration order matches the API's
 * ranking order: exact first, then rotated, then close, then close rotated, then no match.
 */
export type SkuSizeMatch =
  | 'EXACT'
  | 'EXACT_ROTATED'
  | 'CLOSE'
  | 'CLOSE_ROTATED'
  | 'NONE';

/** One of a SKU's finished sizes, with the catalogue string and its dimensions in inches. */
export interface SkuSizeNumeric {
  /** The size exactly as the catalogue stores it, e.g. {@code 8.5x11}. */
  size?: string;
  orientation?: 'portrait' | 'landscape' | 'cube';
  widthIn?: number;
  lengthIn?: number;
  /** Third dimension, only on cube sizes. */
  heightIn?: number;
  /**
   * The sheet the press prints for this same size entry, e.g. {@code 9x12} for a brochure
   * finished at {@code 9x6}. Equal to {@link size} on anything that neither folds nor binds.
   *
   * This is what the design was actually matched against — artwork has to cover the sheet, not
   * the folded result — so a chip naming {@link size} alone reports a size the match was never
   * computed on, and reads as a broken rule next to the buyer's own dimensions.
   */
  flatSize?: string;
  flatWidthIn?: number;
  flatLengthIn?: number;
}

export interface GallerySku {
  defaultQty: number;
  id: number;
  images: string[];
  description: string;
  name: string;
  displayName: string;
  dropdownLabelName: string;
  labelName: string;
  orgId: number;
  revxId: number;
  preConfigured: boolean;
  unitPriceConfigured: boolean;
  typeName?: string;
  image: string;
  qtyLocked: boolean;
  quantities: number[];
  /** Every finished size configured on the SKU, with numeric dimensions. */
  finishedSizes?: SkuSizeNumeric[];
  /**
   * Best size match against the design in the request.
   *
   * Undefined means the visit carried no readable design size and the dimension tier never ran —
   * which is NOT the same as {@code 'NONE'}. Use {@code isDimensionMatch} from
   * {@code utils/skuSizeMatch} rather than testing this directly.
   */
  sizeMatch?: SkuSizeMatch;
  /**
   * The SKU's size that fits the design best — what it was ranked on. Set whenever the dimension
   * tier ran, including when the fit is outside tolerance, so a chip can name the nearest size
   * even for a SKU that does not match.
   */
  closestSize?: SkuSizeNumeric;
  /**
   * How far {@link closestSize} is from the design: 0 is a perfect fit, larger is worse, and the
   * gallery arrives ordered by this ascending. Scale-free, so it is comparable across formats.
   */
  sizeDistance?: number;
  /** Times the current org has ordered this SKU; the secondary ranking tier. */
  orderCount?: number;
  /**
   * True on the single busiest SKU of the whole browsed catalogue — the "Most ordered" badge.
   *
   * Decided server-side because it is a superlative over the entire result, not a property of a
   * row: the busiest SKU may sit on any page, so a client holding one page cannot work it out.
   * At most one row per result carries it, and it can co-occur with a dimension match.
   */
  mostOrdered?: boolean;
}

/** The Canva design parked for a company gallery visit ({@code ?canvaDraftId=}). */
export interface CanvaPublishDraft {
  draftId: string;
  fileCount?: number;
  /** Design trim size in inches, already converted from the PDF's points by the API. */
  designWidthIn?: number;
  designLengthIn?: number;
  designPageCount?: number;
  /** False when the design's pages differ in size, i.e. the size describes page 1 only. */
  designUniformPageSize?: boolean;
}

export interface SkuFilter {
  skuTypes: SkuType[];
}

export enum GALLERY_SKU_ENUM {
  SUMMARY = 'SUMMARY',
  COMPARE = 'COMPARE',
}

export enum BUSINESS_TYPE_ENUM {
  BUSINESS_ESSENTIALS = 'BUSINESS_ESSENTIALS',
  DIRECT_MAIL = 'DIRECT_MAIL',
  MARKETING = 'MARKETING',
  PROMOTIONAL_ITEMS = 'PROMOTIONAL_ITEMS',
  MY_COMPANY = 'MY_COMPANY',
  MY_PRODUCTS = 'MY_PRODUCTS',
}

export enum GALLERY_TYPE_ENUM {
  SKU = 'SKU',
  COMPANY = 'COMPANY',
}

export enum GALLERY_RENDER_ENUM {
  DEFAULT = 'DEFAULT',
  LIST = 'LIST',
}
export interface SkuGalleryLabel {
  active: boolean;
  businessTypes: BUSINESS_TYPE_ENUM[];
  description: string;
  dropDownLabels: SkuGalleryLabel[];
  id: number;
  image: string;
  name: string;
  parentId: number;
  summary: string;
  version: number;
  skuId?: string;
  defaultQty?: number;
  typeName?: string;
  galleryLabelId?: number;
  companyLabelId?: number;
  galleryType?: GALLERY_TYPE_ENUM;
}

export interface SsoBuyerGalleryLabel {
  active: boolean;
  businessTypes: BUSINESS_TYPE_ENUM[];
  description: string;
  dropDownLabels: SkuGalleryLabel[];
  id: number;
  image: string;
  name: string;
  parentId: number;
  summary: string;
  version: number;
  skuId?: string;
  defaultQty?: number;
  typeName?: string;
  orgId?: number;
  galleryLabelId?: number;
  companyLabelId?: number;
  galleryType?: GALLERY_TYPE_ENUM;
}

export interface SkuGalleryLabelUpdateParams {
  active: boolean;
  businessTypes: BUSINESS_TYPE_ENUM[];
  description: string;
  id: number;
  image: string;
  name: string;
  parentId: number | null;
  summary: string;
  version: number;
  isCompany: boolean;
}

export interface SkuCompanyLabelUpdateParams {
  active: boolean;
  description: string;
  id: number;
  image: string;
  name: string;
  version: number;
  isCompany: boolean;
}

export type SkuGalleryLabelCreateParams = Omit<
  SkuGalleryLabelUpdateParams,
  'id' | 'version'
> & { id: undefined; isCompany: boolean };

export const EMPTY_GALLERY_LABEL = (
  isCompany: boolean
): SkuGalleryLabelCreateParams => {
  return {
    id: undefined,
    active: true,
    businessTypes: [],
    description: '',
    image: '',
    name: '',
    parentId: null,
    summary: '',
    isCompany: isCompany,
  };
};

export interface PreConfiguredSkuGallery {
  id: string;
  version: number;
  businessTypes: BUSINESS_TYPE_ENUM[];
  name: string;
  description: string;
  active: boolean;
  image: string;
  defaultQty: number;
}

export interface SkuGalleryLabel {
  galleryLabels: SkuGalleryLabelCount[];
}

export enum SKU_LABEL_ENUM {
  companyLabels = 'companyLabels',
  galleryLabels = 'galleryLabels',
}

export interface PlatformSkuLabel {
  [SKU_LABEL_ENUM.galleryLabels]: SkuGalleryLabelCount[];
  [SKU_LABEL_ENUM.companyLabels]: SkuGalleryLabelCount[];
}

export interface PlatformSkuLabel {
  [SKU_LABEL_ENUM.galleryLabels]: SkuGalleryLabelCount[];
  [SKU_LABEL_ENUM.companyLabels]: SkuGalleryLabelCount[];
}

export interface SkuGalleryLabelCount {
  skuCount: number;
  skuLabel: SkuGalleryLabel;
}

export const BUSINESS_TYPE_COLLECTION = [
  {
    id: BUSINESS_TYPE_ENUM.BUSINESS_ESSENTIALS,
    name: 'Business Essentials',
  },
  {
    id: BUSINESS_TYPE_ENUM.DIRECT_MAIL,
    name: 'Direct Mail',
  },
  {
    id: BUSINESS_TYPE_ENUM.MARKETING,
    name: 'Marketing',
  },
  {
    id: BUSINESS_TYPE_ENUM.PROMOTIONAL_ITEMS,
    name: 'Promo Items',
  },
  {
    id: BUSINESS_TYPE_ENUM.MY_PRODUCTS,
    name: 'My Productions',
  },
];

/* TODO:  ID value ? */
export const MY_COMPANY_TYPE_COLLECTION = [
  {
    id: 1,
    name: 'Marketing',
  },
  {
    id: 103,
    name: 'Store Forms',
  },
  {
    id: 4,
    name: 'HR',
  },
  {
    id: 5,
    name: 'Deli',
  },
  {
    id: 6,
    name: 'Bakery',
  },
  {
    id: 7,
    name: 'Wine & Liquor',
  },
  {
    id: 10,
    name: 'Meat & Seafood',
  },
  {
    id: 8,
    name: 'Produce',
  },
  {
    id: 9,
    name: 'Pharmacy',
  },
];
